import { useState, useEffect } from "react";
import { sendEmailVerification, onIdTokenChanged, Auth } from "firebase/auth";
import { Loader2, Mail } from "lucide-react";
import { Button } from "../ui/button";

export interface VerificationStatusProps {
  auth: Auth;
  onVerified: () => void;
  onSignInClick: () => void;
}

export function VerificationStatus({
  auth,
  onVerified,
  onSignInClick,
}: VerificationStatusProps) {
  const [isSending, setIsSending] = useState(false);
  const [isResent, setIsResent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get the current user's email
  const userEmail = auth.currentUser?.email || "";

  // Listen to auth state changes to detect when email is verified
  useEffect(() => {
    const intervalId = setInterval(() => {
      auth.currentUser?.reload();
      auth.currentUser?.getIdToken(/* forceRefresh */ true);
    }, 10000);

    const unsubscribe = onIdTokenChanged(auth, (user) => {
      if (user?.emailVerified) {
        clearInterval(intervalId);

        // Short delay to show the redirecting state
        setTimeout(() => {
          onVerified();
        }, 100);
      }
    });

    // Clean up when component unmounts
    return () => {
      clearInterval(intervalId);
      unsubscribe();
    };
  }, [auth, onVerified]);

  const handleResendVerification = async () => {
    if (!auth.currentUser) {
      onSignInClick();
      return;
    }

    setIsSending(true);
    setError(null);

    try {
      await sendEmailVerification(auth.currentUser);
      setIsResent(true);
    } catch (err) {
      setError("Failed to send verification email. Please try again later.");
      console.error("Error sending verification email:", err);
    } finally {
      setIsSending(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await auth.signOut();
      onSignInClick();
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center gap-6 p-6 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-yellow-100 text-yellow-600 dark:bg-yellow-900 dark:text-yellow-300">
        <Mail className="h-8 w-8" strokeWidth={2} />
      </div>

      <h1 className="text-2xl font-semibold">Email Verification Required</h1>

      <div className="max-w-md space-y-4">
        <p className="text-muted-foreground">
          Your email address <strong>{userEmail}</strong> needs to be verified
          before you can access the app.
        </p>

        <p className="text-muted-foreground">
          Please check your inbox for a verification email and click the link to
          verify your account.
        </p>

        {isResent && (
          <div className="rounded-md bg-success/10 p-4 dark:bg-success/20">
            <p className="text-success dark:text-success">
              Verification email has been resent. Please check your inbox.
            </p>
          </div>
        )}

        {error && (
          <div className="rounded-md bg-destructive/10 p-4 dark:bg-destructive/20">
            <p className="text-destructive dark:text-destructive">{error}</p>
          </div>
        )}

        <div className="flex flex-col gap-3 pt-4 sm:flex-row sm:justify-center">
          <Button variant="outline" onClick={handleSignOut}>
            Sign Out
          </Button>
          <Button
            onClick={handleResendVerification}
            disabled={isSending}
            className="purple-gradient hover:purple-gradient-hover text-white dark:text-black"
          >
            {isSending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending...
              </>
            ) : (
              "Resend Verification Email"
            )}
          </Button>
        </div>
        <div className="mt-8 text-sm text-muted-foreground">
          <p>
            Already verified your email?{" "}
            <button
              type="button"
              onClick={onSignInClick}
              className="text-primary hover:text-primary/80"
            >
              Sign in again
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
